<template>
  <div>
    <span class="mb-2 block text-sm font-medium text-obsidian-700">{{ t('label') }}</span>

    <div
      v-if="selectedMedia"
      class="flex items-center gap-2 rounded-lg border border-obsidian-200 p-2"
    >
      <div class="size-8 shrink-0 overflow-hidden rounded-md bg-obsidian-700">
        <img
          v-if="selectedMedia.posterFileUrl"
          :src="selectedMedia.posterFileUrl"
          class="h-full w-full object-cover"
          alt=""
        />
        <div v-else class="flex h-full w-full items-center justify-center text-white">
          <VideoIcon class="size-4" />
        </div>
      </div>
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <p class="truncate text-sm font-medium">{{ selectedMedia.title }}</p>
        <MediaEncodingStatusBadge :status="selectedMedia.encodingStatus" size="sm" />
      </div>
      <CVButton variant="secondary" size="sm" @click.prevent="showPreviewModal = true">
        <PlayIcon class="size-4" /> {{ t('preview') }}
      </CVButton>
      <CVButton v-if="!disabled" variant="secondary" size="sm" @click.prevent="showPicker = true">
        {{ t('change') }}
      </CVButton>
      <CVButton v-if="!disabled" variant="danger" size="sm" @click.prevent="handleRemove">
        <Trash2Icon class="size-4" />
      </CVButton>
    </div>

    <div
      v-else
      class="flex items-center justify-between gap-2 rounded-lg border border-dashed border-obsidian-300 p-2"
    >
      <div class="flex min-w-0 flex-1 flex-col">
        <span class="text-sm text-obsidian-500">{{ t('chooseMedia') }}</span>
        <span v-if="fallback" class="truncate text-xs text-obsidian-400">
          {{ t('fallbackHint', { title: fallback.title }) }}
        </span>
      </div>
      <CVButton v-if="!disabled" variant="secondary" size="sm" @click.prevent="showPicker = true">
        {{ t('select') }}
      </CVButton>
    </div>

    <MediaPickerModal v-model="showPicker" @select="handlePickMedia" />

    <Modal v-model="showPreviewModal" :content-class="'!max-w-5xl w-full'" :show-close="true">
      <MediaPlayer v-if="showPreviewModal && selectedMedia" :media-id="selectedMedia.id" />
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import MediaPickerModal from '@/components/media/MediaPickerModal.vue';
import MediaEncodingStatusBadge from '@/components/badges/MediaEncodingStatusBadge.vue';
import MediaPlayer from '@/views/media/MediaPlayer.vue';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import { useField } from 'vee-validate';
import { PlayIcon, Trash2Icon, VideoIcon } from 'lucide-vue-next';
import { ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  name: string;
  initialMedia?: RosMedia | null;
  fallback?: RosMedia | null;
  disabled?: boolean;
}>();

const { setValue } = useField<string | null | undefined>(toRef(props, 'name'));

const selectedMedia = ref<RosMedia | null>(props.initialMedia ?? null);

const { t } = useI18n();

const showPicker = ref(false);
const showPreviewModal = ref(false);

function handlePickMedia(media: RosMedia) {
  selectedMedia.value = media;
  setValue(media.id);
}

function handleRemove() {
  selectedMedia.value = null;
  setValue(null);
}
</script>

<i18n lang="json">
{
  "en": {
    "label": "Promotional video",
    "chooseMedia": "No promotional video selected",
    "fallbackHint": "Falls back to vehicle type video: {title}",
    "select": "Select",
    "change": "Change",
    "preview": "Preview"
  },
  "de": {
    "label": "Werbevideo",
    "chooseMedia": "Kein Werbevideo ausgewählt",
    "fallbackHint": "Nutzt ersatzweise das Fahrzeugtyp-Video: {title}",
    "select": "Auswählen",
    "change": "Ändern",
    "preview": "Vorschau"
  },
  "es": {
    "label": "Vídeo promocional",
    "chooseMedia": "Ningún vídeo promocional seleccionado",
    "fallbackHint": "Usa como alternativa el vídeo del tipo de vehículo: {title}",
    "select": "Seleccionar",
    "change": "Cambiar",
    "preview": "Vista previa"
  }
}
</i18n>
